import React, { useEffect, useMemo } from "react";
import type { RecommendationOptionsInput } from "../../../configurator";
import {
  buildCalibrationAdjustedRecommendationOptions,
  deriveCalibrationSuggestions,
  resolveDisplayRecommendationOptions,
  type CalibratedCoefficientSuggestion,
  type MeasurementRecord,
} from "../../../web-app";
import "./calibration-review-modal.css";
import CcButton from "../components/CcButton";

export interface CalibrationReviewModalProps {
  open: boolean;
  inFitRecords: MeasurementRecord[];
  assumptions: RecommendationOptionsInput;
  onClose: () => void;
  onApply: (adjusted: RecommendationOptionsInput) => void;
}

const CalibrationReviewModal = ({
  open,
  inFitRecords,
  assumptions,
  onClose,
  onApply,
}: CalibrationReviewModalProps) => {
  const suggestions = useMemo(() => {
    if (!open || inFitRecords.length < 2) {
      return [] as CalibratedCoefficientSuggestion[];
    }
    const currentOptions = resolveDisplayRecommendationOptions(assumptions);
    return deriveCalibrationSuggestions(inFitRecords, currentOptions);
  }, [open, inFitRecords, assumptions]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const recordLabel = inFitRecords.length === 1 ? "record" : "records";
  const introText =
    suggestions.length === 0
      ? `Based on ${inFitRecords.length} in-fit measurement ${recordLabel}.`
      : `Based on ${inFitRecords.length} included measurements, these adjustments would reduce mean prediction error. Review before applying to the assumption set.`;

  const handleApply = () => {
    if (suggestions.length === 0) return;
    onApply(buildCalibrationAdjustedRecommendationOptions(assumptions, suggestions));
  };

  return (
    <div
      className="dialog-scrim calibration-review-modal-scrim"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog-card calibration-review-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calibration-review-modal-title"
      >
        <div className="dialog-header calibration-review-modal__header">
          <h2 className="dialog-title" id="calibration-review-modal-title">
            Review suggested calibration
          </h2>
          <CcButton variant="icon" aria-label="Close modal" onClick={onClose}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body calibration-review-body">
          <p>{introText}</p>
          {suggestions.length === 0 ? (
            <p className="calibration-no-adjustments">
              No adjustments suggested based on current in-fit records.
            </p>
          ) : (
            <div className="calibration-suggestion-list">
              {suggestions.map((suggestion) => (
                <div
                  key={`${suggestion.key}-${suggestion.frequencyHz ?? "global"}`}
                  className="calibration-suggestion-row"
                >
                  <span>{suggestion.label}</span>
                  <span className="calibration-suggestion-values mono-value">
                    <span className="calibration-suggestion-current">{suggestion.current}</span>
                    <span className="calibration-suggestion-arrow">→</span>
                    <strong>{suggestion.suggested}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dialog-footer calibration-review-modal__footer">
          <CcButton variant="ghost" onClick={onClose}>
            Cancel
          </CcButton>
          <CcButton variant="primary" onClick={handleApply} disabled={suggestions.length === 0}>
            Apply to assumptions
          </CcButton>
        </div>
      </div>
    </div>
  );
};

export default CalibrationReviewModal;
